/**
 * LightingCommand.js
 * Light Switch + Feeder ekleme/silme komutları
 *
 * Light Switch eklenirken:
 * 1. Component (light switch) oluşturulur
 * 2. Feeder (wire/cable) oluşturulur
 * 3. Three.js sahnesine light switch ve wire eklenir
 */

import BaseWiringCommand from './BaseWiringCommand';
import * as THREE from 'three';
import { createLightSwich } from '../../../factory/symbols/createLightingSymbols';
import { singleCircleBuat, singleSquareBuat } from '../../../factory/symbols/createSocketSymbols';

class LightingCommand extends BaseWiringCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} lightSwitchData - Light switch component verisi
   * @param {object} feederData - Feeder verisi
   * @param {object} sceneData - Three.js scene verisi (light switch position, wire vertices, etc.)
   */
  constructor(action, lightSwitchData, feederData, sceneData) {
    // Determine chosen color: prefer explicit sceneData.color, then metadata color, then fallback
    const chosenColor = (sceneData && sceneData.color) || (lightSwitchData && lightSwitchData.metadata && lightSwitchData.metadata.color) || 0x0045ff;

    super(
      action === 'ADD' ? 'ADD_LIGHT_SWITCH' : 'DELETE_LIGHT_SWITCH',
      action,
      lightSwitchData,
      feederData,
      sceneData,
      {
        componentType: 'LightSwitch',
        cacheMethodSuffix: 'LightSwitch',
        wireColor: chosenColor,
        createWire: true
      }
    );
    // ✅ restore metadata (used to re-enter draw mode after undo)
    this.restoreVertices = (sceneData && (sceneData.restoreVertices || sceneData.wireVertices)) || [];
    this.restoreSource = (sceneData && sceneData.restoreSource) || null;
    this.restoreCommandType = (sceneData && sceneData.restoreCommandType) || null;
    // keep any provided restoreCallback (may be passed via options or sceneData)
    this.restoreCallback = (this.options && this.options.restoreCallback) || (sceneData && sceneData.restoreCallback) || null;
  }

  /**
   * Light switch sembolünü oluştur
   * @override
   */
  createSymbol(scene, lightSwitchData, sceneData) {
    // ✅ DUPLICATE DETECTION: Aynı ID ile light switch zaten varsa yeniden oluşturma
    const lightSwitchId = lightSwitchData?.id;
    if (lightSwitchId) {
      let existingLightSwitch = null;
      scene.traverse(obj => {
        if (obj.userData?.id === lightSwitchId) {
          existingLightSwitch = obj;
        }
      });
      if (existingLightSwitch) {
         return existingLightSwitch;
      }
    }

    // ✅ lightSwitchPosition yoksa lightSwitchInfos'tan al (REDO uyumluluğu)
    let lightSwitchPosition;
    if (sceneData.lightSwitchPosition) {
      lightSwitchPosition = new THREE.Vector3(
        sceneData.lightSwitchPosition[0],
        sceneData.lightSwitchPosition[1],
        sceneData.lightSwitchPosition[2]
      );
    } else if (sceneData.lightSwitchInfos && sceneData.lightSwitchInfos.length > 0) {
      // REDO: lightSwitchInfos array'inden pozisyon al
      const info = sceneData.lightSwitchInfos[0];
      lightSwitchPosition = new THREE.Vector3(
        info.position.x,
        info.position.y,
        info.position.z || 0
      );
    } else if (lightSwitchData?.position) {
      // Fallback: componentData'dan pozisyon al
      lightSwitchPosition = new THREE.Vector3(
        lightSwitchData.position.x,
        lightSwitchData.position.y,
        lightSwitchData.position.z || 0
      );
    } else {
      console.warn('[LightingCommand] createSymbol: lightSwitchPosition bulunamadı, atlanıyor');
      return null;
    }

    const dir = sceneData.dir || 1;
    const color = sceneData.color || this.config.wireColor || 0x0045ff;
    const tempLineUUID = sceneData.tempLineUUID || null;

    // ✅ REDO uyumluluğu: lightSwitchInfos'tan type ve rotateValue al
    const infoItem = sceneData.lightSwitchInfos?.[0];
    const lightSwitchType = sceneData.lightSwitchType || infoItem?.type || 'normal';
    const rotateValue = sceneData.rotateValue ?? infoItem?.rotateValue ?? 0;
    // ✅ stretchLength from sceneData or lightSwitchData metadata
    const stretchLength = sceneData.stretchLength ?? lightSwitchData?.metadata?.stretchLength ?? 0;
    // lightSwitchId zaten fonksiyon başında tanımlandı (duplicate detection için)

    const lightSwitchGroup = createLightSwich(
      scene,
      lightSwitchPosition,
      dir,
      color,
      false, // isPreview
      lightSwitchType,
      tempLineUUID,
      rotateValue,
      lightSwitchId,  // ✅ providedLightSwitchId parametresi
      stretchLength   // ✅ stretchLength parametresi
    );

    // Rotation'ı ayrıca set et
    lightSwitchGroup.rotation.z = sceneData.rotation || 0;

    return lightSwitchGroup;
  }

  /**
   * ✅ Execute with buat recreation support
   * @override
   */
  execute(scene, dispatch, skipRedux = false) {
    super.execute(scene, dispatch, skipRedux);

    // ✅ Buatları yeniden oluştur (REDO için)
    // skipRedux = false ise global redo (Enter sonrası), buatları oluştur
    if (!skipRedux && this.sceneData?.buatInfos && this.sceneData.buatInfos.length > 0) {
 
      const color = this.sceneData?.color || 0x0045ff;

      this.sceneData.buatInfos.forEach(buatInfo => {
        // Bu buat zaten sahnede var mı kontrol et
        let existingBuat = null;
        scene.traverse(obj => {
          if (obj.userData?.id === buatInfo.id) {
            existingBuat = obj;
          }
        });

        if (existingBuat) {
           return;
        }

        const point = new THREE.Vector3(
          buatInfo.position.x,
          buatInfo.position.y,
          buatInfo.position.z || 0
        );

        let buat;
        if (buatInfo.buatType === 'square') {
          buat = singleSquareBuat(scene, point, color, buatInfo.id);
        } else {
          buat = singleCircleBuat(point, buatInfo.panelID, buatInfo.id, color);
          scene.add(buat);
        }

        // userData'yı geri yükle
        if (buat) {
          buat.userData = {
            ...buat.userData,
            id: buatInfo.id,
            type: buatInfo.type || 'lightingBuat',
            buatType: buatInfo.buatType || 'circle',
            lightSwitchLoadId: buatInfo.lightSwitchLoadId,
            wireID: buatInfo.wireID,
            panelID: buatInfo.panelID,
            function: buatInfo.function || 'lighting'
          };
         }
      });
    }
  }

  /**
   * ✅ Undo operation with restore callback support
   * @override
   */
  undo(scene, dispatch, skipRedux = false) {
    

    // ✅ Local undo (skipRedux=true): Single lightSwitch during drawing - use cache
    if (skipRedux) {
      
      super.undo(scene, dispatch, skipRedux);
      return;
    }

    // ✅ Global undo (skipRedux=false): Full wire + lightSwitches after Enter
    // Lighting wire sistemi için özel temizleme
    // sceneData'dan wire ve lightSwitch ID'lerini al
    const wireUUID = this.sceneData?.wireUUID;
    const lightSwitchIds = this.sceneData?.lightSwitchIds || [];
    const wireId = this.sceneData?.wireId;

    

    try {
      // 1. Wire line'ı temizle (UUID ile)
      if (wireUUID) {
        const wireLine = scene.getObjectByProperty('uuid', wireUUID);
        if (wireLine) {
          
          if (wireLine.geometry) wireLine.geometry.dispose();
          if (wireLine.material) wireLine.material.dispose();
          scene.remove(wireLine);
        }
      }

      // 2. Wire ID ile de dene (fallback)
      if (wireId && !wireUUID) {
        scene.traverse(obj => {
          if (obj.userData?.id === wireId || obj.userData?.feederId === wireId) {
            
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) obj.material.dispose();
            if (obj.parent) obj.parent.remove(obj);
          }
        });
      }

      // 3. Tüm lightSwitch'leri temizle
      const lightSwitchesToRemove = [];
      lightSwitchIds.forEach(lightSwitchId => {
        scene.traverse(obj => {
          if (obj.userData?.id === lightSwitchId) {
            lightSwitchesToRemove.push(obj);
          }
        });
      });

      lightSwitchesToRemove.forEach(obj => {
        
        obj.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
        if (obj.parent) obj.parent.remove(obj);
      });

      // 4. Preview wire'ları temizle - SADECE bu command'a ait olanlar
      // ⚠️ CRITICAL: wireID kontrolü ile sadece bu hattın preview wire'larını sil
      const currentWireID = this.sceneData?.wireID || this.sceneData?.wireId;
      const previewWiresToRemove = [];
      scene.traverse(obj => {
        // Preview wire'lar sadece bu command'ın wireID'si ile eşleşmeli
        if (obj.name === 'tempLightWirePreview' && obj.type === 'Line') {
          // tempLightWirePreview isimliler her zaman silinebilir (geçici preview)
          previewWiresToRemove.push(obj);
        } else if (obj.userData?.type === 'wireLighting' && obj.type === 'Line' && currentWireID) {
          // wireLighting tipindekiler SADECE wireID eşleşirse silinmeli
          if (obj.userData?.wireID === currentWireID || obj.userData?.id === currentWireID) {
            previewWiresToRemove.push(obj);
          }
        }
      });

      previewWiresToRemove.forEach(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
        if (obj.parent) obj.parent.remove(obj);
      });

      // 5. Wire marker'ları temizle
      // ✅ wireID (büyük harfli) kullan - wireMarker'lar wireID ile eşleşir
      const wireIDForMarkers = this.sceneData?.wireID || wireId;
      const markersToRemove = [];
      scene.traverse(obj => {
        if (obj.userData?.role === 'wireMarker' && obj.userData?.parentWireId === wireIDForMarkers) {
          markersToRemove.push(obj);
        }
      });

      markersToRemove.forEach(obj => {
        
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
        if (obj.parent) obj.parent.remove(obj);
      });

      // 6. Buat'ları temizle (lightingBuat ve buatCircle)
      // ✅ wireID eşleşmesi VEYA buatInfos id eşleşmesi: branch (ek) buat'ın wireID'si
      // parent hattınki olduğu için wireID ile yakalanamıyordu → id ile de sil.
      const buatIdSet = new Set((this.sceneData?.buatInfos || []).map(bi => bi?.id).filter(Boolean));
      const buatsToRemove = [];
      scene.traverse(obj => {
        if (obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') {
          if (obj.userData?.wireID === this.sceneData?.wireID ||
              (obj.userData?.id && buatIdSet.has(obj.userData.id))) {
            buatsToRemove.push(obj);
          }
        }
      });

      buatsToRemove.forEach(obj => {
        
        obj.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
        if (obj.parent) obj.parent.remove(obj);
      });

      // 7. Fixture hatlarını temizle (eğer varsa)
      const fixturesToRemove = [];
      scene.traverse(obj => {
        if (obj.userData?.parentWireId === wireId || obj.userData?.lightingLoadId === this.sceneData?.lightingLoadId) {
          // Fixture hatları da temizlenmeli
          if (obj.userData?.type === 'fixture' || obj.userData?.function === 'fixture') {
            fixturesToRemove.push(obj);
          }
        }
      });

      fixturesToRemove.forEach(obj => {
        
        obj.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
        if (obj.parent) obj.parent.remove(obj);
      });

    } catch (cleanupErr) {
      console.error('[LightingCommand] Sahne temizleme hatası:', cleanupErr);
    }

    // Defensive cleanup: legacy scene objects
    try {
      const feederId = this.data.after?.feeder?.id || this.data.before?.feeder?.id;
      const compId = this.data.after?.component?.id || this.data.before?.component?.id;

      try {
        const sceneObjectCache = require('../SceneObjectCache').default;
        if (feederId) {
          const wire = sceneObjectCache.getFeeder(feederId) || sceneObjectCache.getWire?.(feederId);
          if (wire && wire.parent) {
            wire.parent.remove(wire);
            if (wire.geometry) wire.geometry.dispose();
            if (wire.material) wire.material.dispose();
          }
          sceneObjectCache.removeFeeder(feederId);
          sceneObjectCache.removeWire?.(feederId);
        }

        if (compId) {
          const comp = sceneObjectCache.getLightSwitch?.(compId) || sceneObjectCache.getFixture?.(compId);
          if (comp && comp.parent) {
            comp.parent.remove(comp);
            comp.traverse(child => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) child.material.dispose();
            });
          }
          sceneObjectCache.removeLightSwitch?.(compId);
        }
      } catch (cacheErr) {
        // Fallback: traverse scene
        if (feederId) {
          scene.traverse((obj) => {
            if (obj.userData?.id === feederId || obj.userData?.feederId === feederId || obj.userData?.lineId === feederId) {
              try {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
                if (obj.parent) obj.parent.remove(obj);
              } catch (err) {
                console.error('[LightingCommand] error removing legacy feeder', err);
              }
            }
          });
        }

        if (compId) {
          scene.traverse((obj) => {
            if (obj.userData?.id === compId || obj.userData?.lightingLoadId === compId) {
              try {
                obj.traverse(child => {
                  if (child.geometry) child.geometry.dispose();
                  if (child.material) child.material.dispose();
                });
                if (obj.parent) obj.parent.remove(obj);
              } catch (err) {
                console.error('[LightingCommand] error removing legacy component', err);
              }
            }
          });
        }
      }
    } catch (cleanupErr) {
      console.error('[LightingCommand] defensive cleanup failed', cleanupErr);
    }

    // Remove Redux entries
    if (!skipRedux) {
      try {
        const { removeFeeder, removeComponent } = require('../../../redux/simpleSupplySlice');
        const { deleteWire } = require('../../../redux/wireSlice');

        const feederId = this.data.after?.feeder?.id || this.data.before?.feeder?.id;
        const compId = this.data.after?.component?.id || this.data.before?.component?.id;
        const wireId = this.sceneData?.wireId || this.sceneData?.wireID || null;

        if (feederId) {
          try { dispatch(removeFeeder(feederId)); } catch (e) { console.warn('[LightingCommand] removeFeeder failed', e); }
        }
        if (compId) {
          try { dispatch(removeComponent(compId)); } catch (e) { console.warn('[LightingCommand] removeComponent failed', e); }
        }
        if (wireId) {
          try { dispatch(deleteWire(wireId)); } catch (e) { console.warn('[LightingCommand] deleteWire failed', e); }
        }
      } catch (err) {
        console.warn('[LightingCommand] Redux cleanup during undo failed', err);
      }
    }

    // ✅ Restore callback: re-enter draw mode with vertices and lightSwitch info
    const restoreFn = (typeof this.restoreCallback === 'function')
      ? this.restoreCallback
      : (this.sceneData && typeof this.sceneData.restoreCallback === 'function')
        ? this.sceneData.restoreCallback
        : (this.options && typeof this.options.restoreCallback === 'function')
          ? this.options.restoreCallback
          : null;

    if (typeof restoreFn === 'function') {
      try {
        const verts = this.restoreVertices || this.sceneData?.restoreVertices || this.sceneData?.wireVertices || [];
        const source = this.restoreSource || this.sceneData?.restoreSource || null;

        // ✅ isRedoContext = false (UNDO) - useBaseWiring'de exit point eklenmemeli
        restoreFn(verts, source, false, this.sceneData);
      } catch (err) {
        console.error('[LightingCommand] restoreCallback threw', err);
      }
    }
  }

  /**
   * JSON'dan LightingCommand oluştur
   */
  static fromJSON(json) {
    const action = json.type === 'ADD_LIGHT_SWITCH' ? 'ADD' : 'DELETE';
    const { component, feeder } = json.data.after || json.data.before;

    // Legacy support: 'lightSwitch' key yerine 'component' key kullanılmış olabilir
    const lightSwitchData = component || (json.data.after || json.data.before).lightSwitch;

    const cmd = new LightingCommand(action, lightSwitchData, feeder, json.sceneData || {});
    // ✅ restore metadata
    cmd.restoreVertices = json.restoreVertices || (json.sceneData && json.sceneData.restoreVertices) || [];
    cmd.restoreSource = (json.sceneData && json.sceneData.restoreSource) || null;
    cmd.restoreCommandType = json.restoreCommandType || (json.sceneData && json.sceneData.restoreCommandType) || null;
    return cmd;
  }

  /**
   * ✅ Serialize to JSON with restore metadata
   * @override
   */
  toJSON() {
    const base = super.toJSON();
    return {
      ...base,
      restoreVertices: this.restoreVertices || [],
      restoreSource: this.restoreSource || null,
      restoreCommandType: this.restoreCommandType || null
    };
  }
}

export default LightingCommand;
